import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PlusCircle, Edit, Trash2, Upload, Download, FileText, Check, AlertCircle, Loader2, ExternalLink, User } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { DocumentService } from '@/services/DocumentService';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from '@/integrations/supabase/client';

const DocumentManager = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<{ id: string; email: string; full_name: string }[]>([]);
  interface Document {
    id: string;
    document_name: string;
    document_type: string;
    file_path: string;
    file_size: number;
    user_id: string;
    company_id?: string;
    is_approved: boolean | null;
    uploaded_at: string | null;
  }

  const [documents, setDocuments] = useState<Document[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    documentType: 'investor' | 'entrepreneur';
    assignedUserId?: string;
  }>({
    title: '',
    description: '',
    documentType: 'investor'
  });

  const { toast } = useToast();
  const { user } = useAuth();

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .order('full_name');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!user) {
        setError('Please sign in to view documents.');
        return;
      }

      const docs = await DocumentService.getDocuments();
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
      setError(error instanceof Error ? error.message : 'Failed to load documents');
      toast({
        title: 'Error',
        description: 'Failed to load documents. Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [user]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      documentType: 'investor'
    });
    setDocumentFile(null);
    setFormErrors({});
  };

  const handleEditDocument = (document: Document) => {
    setSelectedDocument(document);
    setFormData({
      title: document.document_name,
      description: document.document_type,
      documentType: document.document_type as 'investor' | 'entrepreneur',
      assignedUserId: document.user_id
    });
    setEditDialogOpen(true);
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await DocumentService.deleteDocument(documentId);
      toast({
        title: 'Success',
        description: 'Document deleted successfully',
      });
      loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive',
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDocumentTypeChange = (value: string) => {
    setFormData(prev => ({ 
      ...prev, 
      documentType: value as 'investor' | 'entrepreneur' 
    }));
  };

  const handleUserChange = (value: string) => {
    setFormData(prev => ({ 
      ...prev, 
      assignedUserId: value 
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast({
        title: 'Error',
        description: 'File size must be less than 50MB',
        variant: 'destructive'
      });
      return;
    }

    setDocumentFile(file);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!documentFile && !selectedDocument) {
      errors.file = 'Please select a file to upload';
    }

    if (!formData.assignedUserId) {
      errors.assignedUser = 'Please select a user to assign the document to';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please check the form for errors',
        variant: 'destructive'
      });
      return;
    }

    if (!user) {
      toast({
        title: 'Error',
        description: 'You must be signed in to upload documents',
        variant: 'destructive'
      });
      return;
    }
    
    setFormSubmitting(true);
    setError(null);

    try {
      if (selectedDocument) {
        // Update existing document
        await DocumentService.uploadDocument(
          documentFile!,
          formData.title,
          formData.documentType,
          selectedDocument.company_id
        );
        toast({
          title: 'Success',
          description: 'Document updated successfully',
        });
        setEditDialogOpen(false);
      } else if (documentFile) {
        // Upload new document
        await DocumentService.uploadDocument(
          documentFile,
          formData.title,
          formData.documentType,
          undefined,
          formData.assignedUserId
        );
        toast({
          title: 'Success',
          description: 'Document uploaded successfully',
        });
        setUploadDialogOpen(false);
      }

      resetForm();
      loadDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
      setError(error instanceof Error ? error.message : 'Failed to save document');
      toast({
        title: 'Error',
        description: 'Failed to save document',
        variant: 'destructive'
      });
    } finally {
      setFormSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : 'Unknown User';
  };

  const renderDocumentForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        <div className="grid gap-2">
          <Label htmlFor="title">Document Title *</Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className={formErrors.title ? "border-red-500" : ""}
          />
          {formErrors.title && (
            <p className="text-sm text-red-500">{formErrors.title}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="documentType">Document Type *</Label>
          <Select
            value={formData.documentType}
            onValueChange={handleDocumentTypeChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select document type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="investor">Investor</SelectItem>
              <SelectItem value="entrepreneur">Entrepreneur</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="assignedUser">Assign to User *</Label>
          <Select
            value={formData.assignedUserId}
            onValueChange={handleUserChange}
          >
            <SelectTrigger className={formErrors.assignedUser ? "border-red-500" : ""}>
              <SelectValue placeholder="Select a user" />
            </SelectTrigger>
            <SelectContent>
              {users.map(user => (
                <SelectItem key={user.id} value={user.id}>
                  {user.full_name} ({user.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formErrors.assignedUser && (
            <p className="text-sm text-red-500">{formErrors.assignedUser}</p>
          )}
        </div>

        {!selectedDocument && (
          <div className="grid gap-2">
            <Label htmlFor="file">Document File *</Label>
            <Input
              id="file"
              type="file"
              onChange={handleFileChange}
              className={formErrors.file ? "border-red-500" : ""}
            />
            {formErrors.file && (
              <p className="text-sm text-red-500">{formErrors.file}</p>
            )}
            <p className="text-xs text-gray-500">
              Maximum file size: 50MB
            </p>
          </div>
        )}
      </div>
      
      <DialogFooter>
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => {
            resetForm();
            selectedDocument ? setEditDialogOpen(false) : setUploadDialogOpen(false);
          }}
          disabled={formSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={formSubmitting}>
          {formSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {selectedDocument ? 'Updating...' : 'Uploading...'}
            </>
          ) : (
            selectedDocument ? 'Update Document' : 'Upload Document'
          )}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-medium">Documents</h3>
          <p className="text-sm text-gray-500">Manage investor and entrepreneur documents</p>
        </div>
        <Button onClick={() => {
          resetForm();
          setUploadDialogOpen(true);
        }}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Upload Document
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Document Library</CardTitle>
          <CardDescription>View and manage uploaded documents</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : documents.length === 0 ? (
            <div className="py-8 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-gray-500">No documents uploaded yet</p>
              <Button className="mt-4" onClick={() => {
                resetForm();
                setUploadDialogOpen(true);
              }}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Upload Document
              </Button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map(doc => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <FileText className="h-4 w-4 text-gray-400" />
                          {doc.document_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          doc.document_type === 'investor' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {doc.document_type === 'investor' ? 'Investor' : 'Entrepreneur'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          {getUserName(doc.user_id)}
                        </div>
                      </TableCell>
                      <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                      <TableCell>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(`/api/documents/${doc.id}`, '_blank')}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEditDocument(doc)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteDocument(doc.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a new document for investors or entrepreneurs
            </DialogDescription>
          </DialogHeader>
          {renderDocumentForm()}
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>
              Update document details
            </DialogDescription>
          </DialogHeader>
          {renderDocumentForm()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentManager;
